/**
 * Queries / mutations React Query de la console unifiée « Diffusions » (admin).
 *
 * Lecture : la VIEW `v_diffusions` (migration 0143) déjà normalisée → un cast
 * léger `diffDb` évite l'inférence PostgREST profonde (même pattern que
 * `ytDb` dans youtube-queries.ts). Écriture groupée : edge fn `diffusions-bulk`.
 */

import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type {
  DiffusionRow,
  DiffusionStatus,
  DiffusionType,
  DiffusionPlatform,
  DiffusionTarget,
} from '@jeraisonne/db/diffusions';
import { supabase } from '@/lib/supabase';

/** Accès à `v_diffusions` sans inférence PostgREST (cf. note ci-dessus). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const diffDb = supabase as unknown as { from: (table: string) => any };

export const DIFFUSIONS_PAGE_SIZE = 20;

export interface DiffusionsFilters {
  type?: DiffusionType | '';
  platform?: DiffusionPlatform | '';
  status?: DiffusionStatus | '';
}

export interface DiffusionsPage {
  rows: DiffusionRow[];
  total: number;
}

export const diffusionsQueryKey = ['admin', 'diffusions'] as const;

export function useDiffusions(opts: { filters?: DiffusionsFilters; page?: number } = {}) {
  const filters = opts.filters ?? {};
  const page = Math.max(1, Math.floor(opts.page ?? 1));
  const from = (page - 1) * DIFFUSIONS_PAGE_SIZE;
  const to = from + DIFFUSIONS_PAGE_SIZE - 1;

  return useQuery({
    queryKey: [
      ...diffusionsQueryKey,
      filters.type ?? '',
      filters.platform ?? '',
      filters.status ?? '',
      page,
    ] as const,
    queryFn: async (): Promise<DiffusionsPage> => {
      let q = diffDb
        .from('v_diffusions')
        .select('*', { count: 'exact' })
        .order('updated_at', { ascending: false });
      if (filters.type) q = q.eq('diffusion_type', filters.type);
      if (filters.platform) q = q.eq('platform', filters.platform);
      if (filters.status) q = q.eq('status', filters.status);
      const { data, error, count } = await q.range(from, to);
      if (error) throw new Error(error.message);
      return { rows: (data ?? []) as DiffusionRow[], total: count ?? 0 };
    },
    placeholderData: keepPreviousData,
    // Rafraîchit tant qu'une diffusion est en cours de publication.
    refetchInterval: (query) =>
      ((query.state.data as DiffusionsPage | undefined)?.rows ?? []).some(
        (r) => r.status === 'publishing',
      )
        ? 5000
        : false,
  });
}

export interface BulkDiffusionsInput {
  targets: DiffusionTarget[];
  action: 'publish_now' | 'schedule';
  scheduled_at?: string | null;
  privacy?: 'private' | 'unlisted' | 'public';
}

export interface BulkDiffusionsResult {
  results: Array<{
    kind: string;
    source_id: string;
    platform: string;
    ok: boolean;
    status?: string;
    error?: string;
  }>;
  scheduled: number;
  launched: number;
  failed: number;
}

export function useBulkDiffusions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: BulkDiffusionsInput): Promise<BulkDiffusionsResult> => {
      const { data, error } = await supabase.functions.invoke<BulkDiffusionsResult>(
        'diffusions-bulk',
        { body: input as unknown as Record<string, unknown> },
      );
      if (error) {
        let message = error.message;
        try {
          const ctxBody = await (
            error as { context?: { json?: () => Promise<unknown> } }
          ).context?.json?.();
          const errObj = ctxBody as { error?: string; message?: string } | undefined;
          message = errObj?.message ?? errObj?.error ?? message;
        } catch {
          /* garde le message générique */
        }
        throw new Error(message);
      }
      if (!data) throw new Error('Réponse vide de la fonction.');
      return data;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: diffusionsQueryKey }),
  });
}
